import _ from 'lodash';
import { faPaperclip } from '@fortawesome/pro-light-svg-icons/faPaperclip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { getAllAttachments } from 'models/answers/snippet_helpers';
import getHighlightIndices from 'components/customer/answer_panel/search/get_highlight_indices';
import SelectAnswer from 'actions/answers/select_answer';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export const HEIGHT = 32;

export function AnswerListItemBase({ answer, isFocused, searchText }) {
  const ref = useRef();
  const onClick = useOnClick(answer);
  useScrollIntoView(ref, isFocused);

  return (
    <ListItem data-aid="answerListItem" isFocused={isFocused} onClick={onClick} ref={ref}>
      <Ellipsis title={answer.name}>
        <HighlightedName answer={answer} isFocused={isFocused} searchText={searchText} />
      </Ellipsis>
      <Paperclip hasAttachments={getAllAttachments(answer).length > 0} />
    </ListItem>
  );
}

const AnswerListItem = React.memo(AnswerListItemBase);
export default AnswerListItem;

const ListItem = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: 32px;
  padding: 0px 16px;

  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
  ${p => p.isFocused && listItemFocused};
`;

const listItemFocused = css`
  &,
  &:hover {
    background-color: ${p => p.theme.colors.green400};
    color: ${p => p.theme.colors.white};
  }
`;

const Ellipsis = styled.span`
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function useOnClick(answer) {
  const executeAction = useExecuteAction();
  return useCallback(() => {
    executeAction(SelectAnswer, { answer, clearHistory: true });
  }, [answer]);
}

function useScrollIntoView(ref, isFocused) {
  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocused, ref]);
}

const HighlightedName = React.memo(HighlightedNameBase);
function HighlightedNameBase({ answer, isFocused, searchText }) {
  if (!answer.name) {
    return null;
  }

  const text = answer.name;
  const highlights = getHighlightIndices(searchText, answer, ['name']).name;

  let textChunks = [];
  let cursor = 0;

  _.forEach(highlights, (highlight, index) => {
    textChunks.push(<span key={`item-text-${index}`}>{text.slice(cursor, highlight.start)}</span>);
    textChunks.push(
      <Highlight isFocused={isFocused} key={`item-highlight-${index}`}>
        {text.slice(highlight.start, highlight.end)}
      </Highlight>
    );
    cursor = highlight.end;
  });

  if (cursor < text.length) {
    textChunks.push(<span key={'item-text-trailing'}>{text.slice(cursor, text.length)}</span>);
  }

  return <React.Fragment>{textChunks}</React.Fragment>;
}

const Highlight = styled.span`
  color: ${p => (p.isFocused ? p.theme.colors.white : p.theme.colors.green400)};
  font-weight: 600;
`;

function Paperclip({ hasAttachments }) {
  if (!hasAttachments) {
    return null;
  }

  return (
    <PaperclipStyled>
      <FontAwesomeIcon icon={faPaperclip} />
    </PaperclipStyled>
  );
}

const PaperclipStyled = styled.div`
  margin-left: ${p => p.theme.spacing.medium};
`;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/answer_panel/search/answer_list_item.jsx